import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { Modal } from '../../../tailwindComponents/Modal';
import { Button } from '../../../tailwindComponents/Button';
import {
  getStatusHeaderClasses,
  getStatusHeaderBadgeClasses,
  getOutboundStatusColor,
  getInboundStatusColor,
} from '../utils/statusColors';
import { despatchLineCount } from '../../../utils/despatch';
import {
  SupplyChainProcess,
  isOutbound,
  getProcessSteps,
  latestCompleteStepKey,
} from './processStepModel';
import { ProcessStepper } from './ProcessStepper';
import { DeliveryDetails } from './DeliveryDetails';
import { OrderSourceTag } from './OrderSourceTag';
import { StageSummaryGrid, StageSummaryRow } from './FulfilmentStageTracker';
import { DespatchNoteDetails } from './DespatchNoteDetails';
import { PickingConfirmationDetails } from './PickingConfirmationDetails';
import { OutboundShipmentNoteDetails } from './OutboundShipmentNoteDetails';
import { ProofOfDeliveryDetails } from './ProofOfDeliveryDetails';
import { ShipmentNoteDetails } from './ShipmentNoteDetails';
import { GoodsReceivedNoteDetails } from './GoodsReceivedNoteDetails';
import {
  FulfilledExternallySkuList,
  fulfilmentDate,
} from './FulfilledExternallySummary';

interface SupplyChainProcessModalProps {
  process: SupplyChainProcess;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  initialStepKey?: string | null;
  isDesktop: boolean;
  isTablet: boolean;
}

function SimpleSection({ rows }: { rows: StageSummaryRow[] }) {
  return (
    <div className='px-6 py-5'>
      <StageSummaryGrid rows={rows} />
    </div>
  );
}

function formatOrderTotal(amount: number, currency: string): string {
  if (!currency) {
    return `${amount}`;
  }
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(amount);
}

function SalesOrderSection({
  salesOrder,
}: {
  salesOrder: NonNullable<OutboundProcess['salesOrder']>;
}) {
  return (
    <div className='space-y-5 px-6 py-5'>
      <StageSummaryGrid
        rows={[
          {
            value: salesOrder.orderNumber || salesOrder.externalOrderId || '—',
            label: 'Order #',
          },
          { value: salesOrder.source || '—', label: 'Source' },
          { value: salesOrder.customerName || '—', label: 'Customer' },
          {
            value: salesOrder.orderPlacedAt
              ? dayjs(salesOrder.orderPlacedAt).format('DD MMM YYYY')
              : '—',
            label: 'Order Placed',
          },
          {
            value: formatOrderTotal(salesOrder.totalPrice, salesOrder.currency),
            label: 'Order Total',
          },
          { value: salesOrder.lines?.length ?? 0, label: 'Lines' },
        ]}
      />
      {salesOrder.lines?.length ? (
        <div>
          <div className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-500'>
            Order Lines
          </div>
          <ul className='divide-y divide-gray-100 rounded-lg border border-gray-100'>
            {salesOrder.lines.map((line, index) => (
              <li
                key={`${line.skuId}-${index}`}
                className='flex items-center justify-between px-3 py-2 text-sm'
              >
                <span className='text-gray-900'>
                  {line.skuName || line.skuId}
                </span>
                <span className='text-gray-500'>× {line.quantity}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {salesOrder.comment ? (
        <div>
          <div className='mb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
            Comment
          </div>
          <p className='text-sm text-gray-700'>{salesOrder.comment}</p>
        </div>
      ) : null}
    </div>
  );
}

function OrderSection({ process }: { process: SupplyChainProcess }) {
  if (isOutbound(process)) {
    const { salesOrder, despatchNote } = process;
    if (salesOrder) {
      return <SalesOrderSection salesOrder={salesOrder} />;
    }
    return (
      <SimpleSection
        rows={[
          {
            value: despatchNote?.purchaseOrderNumber ?? '—',
            label: 'PO Number',
          },
          {
            value:
              despatchNote?.customer?.name ?? despatchNote?.customerName ?? '—',
            label: 'Customer',
          },
          {
            value: despatchNote?.despatchNoteNumber ?? '—',
            label: 'Despatch Note #',
          },
          { value: despatchLineCount(despatchNote), label: 'Lines' },
        ]}
      />
    );
  }
  const { shipmentNote } = process;
  return (
    <SimpleSection
      rows={[
        { value: shipmentNote?.supplier?.name ?? '—', label: 'Supplier' },
        {
          value: shipmentNote?.shipmentNoteNumber ?? '—',
          label: 'Order Ref',
        },
        { value: shipmentNote?.skus?.length ?? 0, label: 'SKUs' },
      ]}
    />
  );
}

function TransportSection({ process }: { process: SupplyChainProcess }) {
  const transportOrderId = isOutbound(process)
    ? process.transportOrderId
    : null;
  const transportOrderStatus = isOutbound(process)
    ? process.transportOrderStatus
    : null;
  return (
    <SimpleSection
      rows={[
        {
          value: transportOrderId ? 'Booked' : 'Not booked',
          label: 'Transport',
        },
        { value: transportOrderStatus ?? '—', label: 'Status' },
      ]}
    />
  );
}

function FulfilledExternallySection({ process }: { process: OutboundProcess }) {
  const { fulfilledExternally } = process;
  if (!fulfilledExternally) {
    return (
      <SimpleSection
        rows={[
          { value: 'Shipped outside Flox', label: 'Fulfilled externally' },
        ]}
      />
    );
  }
  return (
    <div className='space-y-5 px-6 py-5'>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          Fulfilled externally
        </p>
        <p className='text-xs text-gray-400'>Shipped outside Flox</p>
      </div>
      <StageSummaryGrid
        rows={[
          {
            value: dayjs(fulfilmentDate(fulfilledExternally)).format(
              'DD MMM YYYY'
            ),
            label: 'Fulfilment date',
          },
          { value: fulfilledExternally.skus?.length ?? 0, label: 'Lines' },
        ]}
      />
      {fulfilledExternally.skus?.length ? (
        <div>
          <div className='mb-2 text-xs font-medium uppercase tracking-wide text-gray-500'>
            SKUs
          </div>
          <FulfilledExternallySkuList skus={fulfilledExternally.skus} />
        </div>
      ) : null}
      {fulfilledExternally.comment ? (
        <div>
          <div className='mb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
            Comment
          </div>
          <p className='text-sm text-gray-700'>{fulfilledExternally.comment}</p>
        </div>
      ) : null}
    </div>
  );
}

function StepBody({
  process,
  stepKey,
  onClose,
  isDesktop,
  isTablet,
}: {
  process: SupplyChainProcess;
  stepKey: string;
  onClose: () => void;
  isDesktop: boolean;
  isTablet: boolean;
}): JSX.Element {
  const shared = { onClose, embedded: true, isDesktop, isTablet };
  const notYet = (label: string) => (
    <SimpleSection rows={[{ value: 'Not started yet', label }]} />
  );

  if (isOutbound(process)) {
    switch (stepKey) {
      case 'despatch':
        return process.despatchNote ? (
          <DespatchNoteDetails selectedOutboundProcess={process} {...shared} />
        ) : (
          notYet('Despatch')
        );
      case 'picking':
        return process.pickingConfirmation ? (
          <PickingConfirmationDetails
            selectedOutboundProcess={process}
            {...shared}
          />
        ) : (
          notYet('Picking')
        );
      case 'shipment':
        return process.shipmentNote ? (
          <OutboundShipmentNoteDetails
            selectedOutboundProcess={process}
            {...shared}
          />
        ) : (
          notYet('Shipment')
        );
      case 'delivery':
        return process.goodsReceivedNote ? (
          <ProofOfDeliveryDetails
            selectedOutboundProcess={process}
            {...shared}
          />
        ) : (
          notYet('Delivery')
        );
      case 'transport':
        return <TransportSection process={process} />;
      case 'fulfilledExternally':
        return <FulfilledExternallySection process={process} />;
      default:
        return <OrderSection process={process} />;
    }
  }

  switch (stepKey) {
    case 'shipment':
      return (
        <ShipmentNoteDetails selectedInboundProcess={process} {...shared} />
      );
    case 'grn':
      return process.goodsReceivedNote ? (
        <GoodsReceivedNoteDetails
          selectedInboundProcess={process}
          {...shared}
        />
      ) : (
        notYet('Goods received note')
      );
    default:
      return <OrderSection process={process} />;
  }
}

export function SupplyChainProcessModal({
  process,
  isOpen,
  setIsOpen,
  initialStepKey,
  isDesktop,
  isTablet,
}: SupplyChainProcessModalProps) {
  const steps = useMemo(() => getProcessSteps(process), [process]);
  const [selectedKey, setSelectedKey] = useState<string>(
    initialStepKey ?? latestCompleteStepKey(steps)
  );

  const statusColor = isOutbound(process)
    ? getOutboundStatusColor(process.status)
    : getInboundStatusColor(process.status);

  const heading = isOutbound(process) ? 'Despatch' : 'Shipment';

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      headerClassName={getStatusHeaderClasses(statusColor)}
      className='max-w-4xl h-[90vh]'
      stableHeight
      title={
        <div className='flex items-center gap-3'>
          <span>
            {heading} · {process.processNumber}
          </span>
          <span className={getStatusHeaderBadgeClasses(statusColor)}>
            {process.status}
          </span>
          {isOutbound(process) && (
            <OrderSourceTag source={process.salesOrder?.source} />
          )}
        </div>
      }
    >
      <div className='flex-none pt-4'>
        <ProcessStepper
          steps={steps}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
        />
      </div>
      <div className='flex-none px-6 pt-2'>
        <DeliveryDetails process={process} />
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto'>
        <StepBody
          process={process}
          stepKey={selectedKey}
          onClose={() => setIsOpen(false)}
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      </div>
      <div className='flex flex-none justify-end gap-3 px-6 py-4 border-t border-gray-200'>
        <Button
          label='Close'
          intent='secondary'
          size='sm'
          onClick={() => setIsOpen(false)}
        />
      </div>
    </Modal>
  );
}
